import React, {useEffect, useState, useMemo} from "react";

import {Navigate, useNavigate} from "react-router-dom";
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {FixedRating} from "components/Rating";
import Button from "components/Button";
import Footer from "../../../components/Safra/Footer";

import {cpf} from "cpf-cnpj-validator";

import TextInput from "../../../components/TextInput";
import DateInput from "../../../components/DateInput";

import {
    CheckIn,
    Container,
    Line,
    LocationMap,
    Points,
    Resumo,
    Title,
    ValuePoints,
    Values,
    GuestTitle,
    GuestSubTitle,
    GuestSectionTitle,
    CheckboxWrapper,
    Checkbox,
    RefundBox,
} from "./styles";
import Icon from 'components/Safra/Icon';

import format from "date-fns/format";
import subDays from "date-fns/subDays";
import isAfter from "date-fns/isAfter";
import {formatCurrency} from "../../../utils/functions";
import {LoaderSearchImage} from "../../../components/Loader";
import {useAuth} from "../../../contexts/Safra/AuthProvider";
import {useApp} from "../../../contexts/AppContext";
import {checkoutHotelAction, setTravellersHotelAction} from "../../../contexts/Safra/actions";
import {useTranslation} from "../../../contexts/Safra/TranslationContext";
import {ModalApp} from "../../../components/Safra/Modal";
import { trackHotelGuests } from "../../../utils/analytics";
import { useUser } from "contexts/Safra/UserProvider";

export default function HotelResume() {
    const [openReserveDetails, setOpenReserveDetails] = useState(false);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [msgError, setMsgError] = useState("");
    const [btnContinuarDisabled, setBtnContinuarDisabled] = useState(true);

    const { hotelState, hotelDispatch } = useApp();
    const { datesSearchHotel, room, hotel, travellers, offersHotel } = hotelState;

    const { customerId, userData } = useAuth();

    const customerData = {
        firstName: userData?.nome || '',
        lastName: userData?.sobrenome || '',
        document: userData?.cpf || '',
    };

    const [isUserGuest, setIsUserGuest] = useState(false);
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [messageModalError, setMessageModalError] = useState("");

    const handleModal = (message) => {
        setOpen(!open);
        setMessageModalError(message);
    };

    const cancellationInfo = useMemo(() => {
        const resInfo = room.reservationInfo;
        const cancelDeadline = resInfo?.cancelDeadLineDateValues;
        const today = new Date();
        let isFreeCancellation = false;
        let deadlineFormatted = '';

        const isIrrevocable = resInfo?.irrevocableGuarantee === true;
        
        const isNonRefundable = Array.isArray(resInfo?.cancellationPolicies) 
            ? resInfo.cancellationPolicies.some(policy => policy?.nonRefundable === true) : false;

        if (!isIrrevocable && !isNonRefundable && cancelDeadline) {
            const deadlineDate = new Date(
                cancelDeadline.year,
                cancelDeadline.month - 1,
                cancelDeadline.day,
                cancelDeadline.hour,
                cancelDeadline.minutes
            );
            const freeCancellationUntil = subDays(deadlineDate, 3);
            isFreeCancellation = isAfter(freeCancellationUntil, today);
            deadlineFormatted = format(freeCancellationUntil, "dd/MM");
        }

        if (isFreeCancellation) {
            return {
                title: t('safra.hotel_resume.cancellation.free_title', { date: deadlineFormatted }),
                text: t('safra.hotel_resume.cancellation.free_text', { date: deadlineFormatted }),
                isFree: true
            };
        }

        return {
            title: t('safra.hotel_resume.cancellation.non_refundable_title'),
            text: t('safra.hotel_resume.cancellation.non_refundable_text'),
            isFree: false
        };
    }, [room, datesSearchHotel, t]);

    useEffect(() => {
        const syncHeight = () => {
            const height = document.body.scrollHeight;
            window.parent.postMessage(height, "*");
        };
        setTimeout(syncHeight, 1000);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (window.parent !== window) {
            window.parent.postMessage({ action: "scrollToTop" }, "*");
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        if (hotel.name && room.name) {
            trackHotelGuests({
                hotel_name: hotel.name,
                room_name: room.name,
                points: room.amount.points,
                total: room.amount.points * datesSearchHotel.days,
                check_in: datesSearchHotel?.startDate ? format(datesSearchHotel.startDate, "dd/MM/yyyy") : '',
                check_out: datesSearchHotel?.endDate ? format(datesSearchHotel.endDate, "dd/MM/yyyy") : '',
                nights: datesSearchHotel?.days,
                stars: hotel.stars,
                hosts: travellers?.length
            });
        }
        setTimeout(() => {
            setLoading(false);
        }, 2000);
    }, []);

    window.parent.postMessage(900, "*");

    if (!hotel.name) {
        return <Navigate to="/hotels-search-result" />;
    }

    const externalProperties =
        room.externalProperties && JSON.parse(room?.externalProperties);

    const getCancelData = (e) => {
        let _day = String(e.day).padStart(2, "0");
        let _month = String(e.month).padStart(2, "0");
        let _year = String(e.year).padStart(2, "0");

        return "Cancelamento grátis: até " + _day + "/" + _month + "/" + _year;
    };

    if (loading) return <LoaderSearchImage />;

    return (
        <Container>
            <GuestTitle>{t('safra.hotel_resume.title')}</GuestTitle>
            <GuestSubTitle>{t('safra.hotel_resume.subtitle')}</GuestSubTitle>

            <Formik
                initialValues={{
                    travellers: [travellers[0]],
                }}
                validationSchema={Yup.object({
                    travellers: Yup.array().of(
                        Yup.object().shape({
                            firstName: Yup.string()
                                .trim()
                                .required('Primeiro nome é obrigatório')
                                .min(2, 'O nome deve ter no mínimo 2 caracteres')
                                .max(29, 'O nome deve ter no máximo 29 caracteres')
                                .matches(/^[a-zA-Z\u00C0-\u00FF\s]+$/, 'O nome não deve conter números ou caracteres especiais'),
                            lastName: Yup.string()
                                .trim()
                                .required('Último nome é obrigatório')
                                .min(2, 'O sobrenome deve ter no mínimo 2 caracteres')
                                .max(29, 'O sobrenome deve ter no máximo 29 caracteres')
                                .matches(/^[a-zA-Z\u00C0-\u00FF\s]+$/, 'O sobrenome não deve conter números ou caracteres especiais'),
                            birthDate: Yup.string()
                                .required("Data de nascimento é obrigatória")
                                .test("is-valid-date", "Data inválida", function (value) {
                                    if (!value || !/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return true;
                                    const [day, month, year] = value.split("/").map(Number);
                                    const date = new Date(year, month - 1, day);
                                    return (
                                        date.getFullYear() === year &&
                                        date.getMonth() === month - 1 &&
                                        date.getDate() === day
                                    );
                                })
                                .test(
                                    "is-not-future-date",
                                    "Data de nascimento não pode ser no futuro",
                                    function (value) {
                                        if (!value) return true;
                                        const [day, month, year] = value.split("/").map(Number);
                                        const birthDate = new Date(year, month - 1, day);
                                        return birthDate <= datesSearchHotel.startDate;
                                    }
                                )
                                .test("is-not-too-old", "Data de nascimento inválida", function (value) {
                                    if (!value) return true;
                                    const [day, month, year] = value.split("/").map(Number);
                                    const birthDate = new Date(year, month - 1, day);
                                    const travelStartDate = datesSearchHotel.startDate;
                                    let age = travelStartDate.getFullYear() - birthDate.getFullYear();
                                    const m = travelStartDate.getMonth() - birthDate.getMonth();
                                    if (m < 0 || (m === 0 && travelStartDate.getDate() < birthDate.getDate())) {
                                        age--;
                                    }
                                    return age >= 0 && age < 120;
                                }),
                        })
                    ),
                })}
                onSubmit={async (values) => {
                    try {
                        await new Promise((r) => setTimeout(r, 500));

                        const guest1 = values.travellers[0];

                        const allTravellers = travellers.map((t, index) => {
                            return {
                                ...t,
                                firstName: guest1.firstName,
                                lastName: guest1.lastName,
                                birthDate: guest1.birthDate,
                            };
                        });

                        hotelDispatch(
                            setTravellersHotelAction(allTravellers)
                        );

                        setLoading(true);

                        let totalPayment = {
                            points: hotel.rooms[0].amount.points,
                            cash: hotel.rooms[0].amount.BRL,
                        };

                        let installmentSelected = 1;

                        let cardSelected = {
                            gateway_card_key: "",
                        };

                        allTravellers.document = cpf.generate();
                        allTravellers.gender = "male";

                        let checkout = await checkoutHotelAction({
                            customerId,
                            hotel,
                            room,
                            cardSelected,
                            totalPayment,
                            installmentSelected,
                            travellers: allTravellers,
                            offersHotel,
                            userDocument: userData?.cpf,
                        });

                        if (checkout?.data && checkout?.hash) {
                            window.parent.postMessage({
                                'message': 'start-checkout',
                                payload: checkout.data,
                                hash: checkout.hash
                            }, '*')
                            return;
                        }

                        if (checkout.status === 201) {
                            let order_uuid =
                                checkout.data.data.order_uuid;

                            let checkoutUrl =
                                process.env.REACT_APP_SAFRA_PASSAGEM +
                                "/loja/resumo-hotel/" +
                                order_uuid;

                            window.top.location.href = checkoutUrl;
                        } else {
                            navigate("/error-checkout");
                        }
                    } catch (err) {
                        handleModal(err?.message);
                    }
                }}
            >
                {({
                      values,
                      setFieldValue,
                      errors,
                      touched,
                      setFieldTouched,
                      isValid,
                      dirty,
                  }) => {
                    useEffect(() => {
                        if (isValid && dirty) {
                            setBtnContinuarDisabled(false);
                        } else {
                            setBtnContinuarDisabled(true);
                        }
                    }, [isValid, dirty]);

                    return (
                        <Form>
                            <div className="mb-4">
                                <GuestSectionTitle>
                                    Hóspede 1
                                </GuestSectionTitle>

                                <CheckboxWrapper>
                                    <Checkbox
                                        type="checkbox"
                                        checked={isUserGuest}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setIsUserGuest(checked);
                                            if (checked) {
                                                setFieldValue(`travellers.0.firstName`, customerData.firstName);
                                                setFieldValue(`travellers.0.lastName`, customerData.lastName);
                                            } else {
                                                setFieldValue(`travellers.0.firstName`, "");
                                                setFieldValue(`travellers.0.lastName`, "");
                                            }
                                        }}
                                    />
                                    Eu sou um dos hóspedes
                                </CheckboxWrapper>

                                <div className="col-12">
                                    <TextInput
                                        placeholder="Primeiro nome"
                                        name={`travellers.0.firstName`}
                                        value={values.travellers[0].firstName}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^a-zA-Z\u00C0-\u00FF\s]/g, "");
                                            if (val.length <= 29) {
                                                setFieldValue(`travellers.0.firstName`, val);
                                            }
                                        }}
                                        onBlur={() => setFieldTouched(`travellers.0.firstName`, true)}
                                        error={touched?.travellers?.[0]?.firstName && errors?.travellers?.[0]?.firstName}
                                    />
                                </div>

                                <div className="col-12">
                                    <TextInput
                                        placeholder="Último sobrenome"
                                        name={`travellers.0.lastName`}
                                        value={values.travellers[0].lastName}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^a-zA-Z\u00C0-\u00FF\s]/g, "");
                                            if (val.length <= 29) {
                                                setFieldValue(`travellers.0.lastName`, val);
                                            }
                                        }}
                                        onBlur={() => setFieldTouched(`travellers.0.lastName`, true)}
                                        error={touched?.travellers?.[0]?.lastName && errors?.travellers?.[0]?.lastName}
                                    />
                                </div>

                                <div className="col-12">
                                    <DateInput
                                        placeholder="Data de nascimento"
                                        name={`travellers.0.birthDate`}
                                        value={values.travellers[0].birthDate}
                                        onChange={(e) => setFieldValue(`travellers.0.birthDate`, e.target.value)}
                                        onBlur={() => setFieldTouched(`travellers.0.birthDate`, true)}
                                        error={touched?.travellers?.[0]?.birthDate && errors?.travellers?.[0]?.birthDate}
                                        standalone
                                    />
                                </div>
                            </div>

                            <RefundBox>
                                <div className="icon">
                                    <Icon name="info" size={20} color="black" />
                                </div>
                                <div className="content">
                                    <h4>{cancellationInfo.title}</h4>
                                    <p>{cancellationInfo.text}</p>
                                </div>
                            </RefundBox>

                            <Button
                                disabled={btnContinuarDisabled}
                                style={{
                                    backgroundColor: btnContinuarDisabled ? "var(--button-disabled-color)" : "var(--primary-color)",
                                    textTransform: "uppercase",
                                    border: "none",
                                    opacity: btnContinuarDisabled ? 0.5 : 1,
                                    transition: "background-color 0.2s ease",
                                }}
                                type="submit"
                            >
                                {t('safra.passage_resume.cta_finish')}
                            </Button>
                        </Form>
                    );
                }}
            </Formik>

            <ModalApp show={open} handleModal={() => handleModal("")}>
                <Container>
                    <p style={{ color: "var(--text-color)" }}>
                        Para continuar sua reserva, preencha as informações
                        obrigatórios.
                        <br />
                        <br />
                        {messageModalError !== ""
                            ? messageModalError
                            : t('safra.hotel_resume.validation.error_processing')}
                    </p>
                </Container>
            </ModalApp>

            <Footer />
        </Container>
    );
}
